import React from 'react';
import type { BomPart, RiskEntry } from '../../services/industrializationService';
import { CostConfirmBar, GenerateButton, RISK_TYPE_LABELS, SEVERITY_COLORS, ToVerifyBadge, type StepProps } from './shared';

const RiskRegisterStep: React.FC<StepProps> = ({
  dossier,
  isRunning,
  costEstimate,
  showCostConfirm,
  onRequestGenerate,
  onConfirmGenerate,
  onCancelGenerate,
}) => {
  const risks: RiskEntry[] = dossier?.risk_register ?? [];
  const bom: BomPart[] = dossier?.bom ?? [];
  const hasBom = bom.length > 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">Step 4 — Sourcing Risk Register</h3>
        {showCostConfirm ? (
          <CostConfirmBar
            costEstimate={costEstimate}
            confirmLabel="Assess Sourcing Risks"
            onConfirm={onConfirmGenerate}
            onCancel={onCancelGenerate}
          />
        ) : (
          <GenerateButton
            label="Assess Sourcing Risks"
            isRunning={isRunning}
            onRequest={onRequestGenerate}
            disabled={!hasBom}
          />
        )}
      </div>

      {risks.length > 0 ? (
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-slate-100">
              <th className="text-left py-1 px-2 font-medium text-slate-600">Part</th>
              <th className="text-left py-1 pr-2 font-medium text-slate-600">Risk Type</th>
              <th className="text-left py-1 pr-2 font-medium text-slate-600">Severity</th>
              <th className="text-left py-1 font-medium text-slate-600">Note</th>
            </tr>
          </thead>
          <tbody>
            {risks.map(risk => (
              <tr key={risk.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-1 px-2 text-xs">
                  {risk.part_name}
                  {risk.to_verify && <ToVerifyBadge />}
                </td>
                <td className="py-1 pr-2 text-xs text-slate-600">
                  {RISK_TYPE_LABELS[risk.risk_type] ?? risk.risk_type}
                </td>
                <td className="py-1 pr-2">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${SEVERITY_COLORS[risk.severity] ?? ''}`}>
                    {risk.severity}
                  </span>
                </td>
                <td className="py-1 text-xs text-slate-500">{risk.note ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-xs text-slate-400 italic">
          {isRunning
            ? 'Assessing risks…'
            : 'No risk entries yet. Generate the BoM first (Step 2), then click "Assess Sourcing Risks".'}
        </p>
      )}
    </div>
  );
};

export default RiskRegisterStep;
